'use client';

import React, { useEffect, useState } from 'react';
import {
  getSectionComponent,
  isValidSection,
  type PageSection,
} from '@/lib/storefront/section-library';
import {
  pageBackgroundStyle,
  pageDesignCssVars,
  pageFontFaceCss,
  type PageDesignTokens,
  type PageSettings,
} from '@/lib/storefront/page-settings';
import { sectionResponsiveCss } from '@/lib/storefront/section-style';

const CACHE_PREFIX = 'fleetava_published_page:';
const META_ATTR = 'data-builder-page-meta';

function cacheKey(slug: string): string {
  return `${CACHE_PREFIX}${slug}:${typeof window === 'undefined' ? '' : window.location.host}`;
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export interface PublishedPage {
  name: string;
  sections: PageSection[];
  settings: PageSettings;
  designTokens: PageDesignTokens;
  publishedAt?: string | null;
}

function normalizePublishedPage(raw: any): PublishedPage | null {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    return {
      name: '',
      sections: raw.filter(isValidSection),
      settings: {},
      designTokens: {},
    };
  }
  if (!isRecord(raw)) return null;
  return {
    name: typeof raw.name === 'string' ? raw.name : '',
    sections: Array.isArray(raw.sections) ? raw.sections.filter(isValidSection) : [],
    settings: isRecord(raw.settings) ? raw.settings : {},
    designTokens: isRecord(raw.designTokens)
      ? raw.designTokens
      : isRecord(raw.design_tokens)
        ? raw.design_tokens
        : {},
    publishedAt: typeof raw.publishedAt === 'string'
      ? raw.publishedAt
      : typeof raw.published_at === 'string'
        ? raw.published_at
        : null,
  };
}

function readCache(slug: string): PublishedPage | null {
  try {
    const raw = window.sessionStorage.getItem(cacheKey(slug));
    if (!raw) return null;
    return normalizePublishedPage(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeCache(slug: string, page: PublishedPage): void {
  try {
    window.sessionStorage.setItem(cacheKey(slug), JSON.stringify(page));
  } catch {
    /* cache is best-effort */
  }
}

function clearCache(slug: string): void {
  try {
    window.sessionStorage.removeItem(cacheKey(slug));
  } catch {
    /* cache is best-effort */
  }
}

export interface PublishedPageState {
  page: PublishedPage | null;
  sections: PageSection[];
  /**
   * True until the first answer arrives. The storefront uses this to avoid
   * painting the built-in design before a custom page replaces it.
   */
  loading: boolean;
  /** Set when a signed preview link is invalid, expired or points at no draft. */
  error: string | null;
}

/**
 * Loads a published page configured in the admin site builder. Pass `null` to
 * skip loading (for routes that can never be a builder page). Returns a null
 * page when the agency has never published that page, in which case the
 * storefront falls back to its built-in design.
 *
 * Pass a `previewToken` to read the unpublished draft through the signed
 * preview endpoint instead. Preview responses are never written to the public
 * session cache.
 *
 * The last answer is kept in sessionStorage so in-app navigation and reloads
 * render the right page on the very first paint instead of flashing the
 * previous page or the built-in template.
 */
export function usePublishedPage(slug: string | null, previewToken?: string | null): PublishedPageState {
  const preview = previewToken?.trim() || null;
  const [entry, setEntry] = useState<{
    slug: string | null;
    preview: string | null;
    page: PublishedPage | null;
    loaded: boolean;
    error: string | null;
  }>(() => {
    const page = slug && !preview ? readCache(slug) : null;
    return { slug, preview, page, loaded: slug === null || page !== null, error: null };
  });

  // Route or preview changed: adopt the cached copy (or the loading state) in
  // the same render, so the previous page is never painted under the new URL.
  if (entry.slug !== slug || entry.preview !== preview) {
    const page = slug && !preview ? readCache(slug) : null;
    setEntry({ slug, preview, page, loaded: slug === null || page !== null, error: null });
  }

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    (async () => {
      try {
        const url = preview
          ? `/api/site-preview/${encodeURIComponent(slug)}?token=${encodeURIComponent(preview)}`
          : `/api/site-config/${encodeURIComponent(slug)}`;
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          if (cancelled) return;
          if (preview) {
            const data = await res.json().catch(() => ({}));
            setEntry({
              slug,
              preview,
              page: null,
              loaded: true,
              error: typeof data?.error === 'string'
                ? data.error
                : res.status === 404
                  ? 'This page has no draft yet.'
                  : 'This preview link is invalid or has expired.',
            });
            return;
          }
          if (res.status === 403 || res.status === 404 || res.status === 410) {
            // Paused (403) or missing pages must not keep a stale published cache.
            clearCache(slug);
            setEntry({
              slug,
              preview,
              page: null,
              loaded: true,
              error: res.status === 403 ? 'This agency is temporarily unavailable.' : null,
            });
          } else {
            setEntry((current) => (current.slug === slug && current.preview === preview
              ? { ...current, loaded: true }
              : current));
          }
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        const page = normalizePublishedPage(data?.page);
        setEntry({ slug, preview, page, loaded: true, error: null });
        if (preview) {
          // Drafts must never leak into the published cache.
          clearCache(slug);
        } else if (page) {
          writeCache(slug, page);
        } else {
          clearCache(slug);
        }
      } catch {
        /* Keep a cached page on a network error; the API being down should not blank the site. */
        if (!cancelled) {
          setEntry((current) => (current.slug === slug && current.preview === preview
            ? {
                ...current,
                loaded: true,
                error: preview ? 'Could not load the preview. Check your connection and try again.' : current.error,
              }
            : current));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, preview]);

  const current = entry.slug === slug && entry.preview === preview ? entry : null;

  return {
    page: current?.page ?? null,
    sections: current?.page?.sections ?? [],
    loading: Boolean(slug) && (!current || !current.loaded),
    error: current?.error ?? null,
  };
}

/**
 * Applies the page's SEO fields to the document. Tags are marked so navigating
 * away removes only the metadata the builder created, leaving other tags alone.
 */
export function usePublishedPageMetadata(page: PublishedPage | null, siteName: string, noIndex = false): void {
  useEffect(() => {
    if (!page) return;

    const previousTitle = document.title;
    const settings = page.settings ?? {};
    const title = typeof settings.title === 'string' && settings.title.trim()
      ? settings.title.trim()
      : page.name && page.name !== 'Home'
        ? `${page.name} · ${siteName}`
        : siteName;
    if (title) document.title = title;

    interface MetaSnapshot {
      tag: HTMLMetaElement;
      created: boolean;
      previousContent: string | null;
      previousMarker: string | null;
    }

    const snapshots = new Map<string, MetaSnapshot>();
    const setMeta = (attribute: 'name' | 'property', key: string, content: string) => {
      const selector = `meta[${attribute}="${key}"]`;
      const managed = snapshots.get(selector);
      if (managed) {
        managed.tag.setAttribute('content', content);
        return;
      }

      // Reuse an existing tag (for example the description in index.html) and
      // restore it on cleanup. Creating a second tag would leave duplicate
      // metadata in the document.
      const existing = document.head.querySelector<HTMLMetaElement>(selector);
      if (existing) {
        snapshots.set(selector, {
          tag: existing,
          created: false,
          previousContent: existing.getAttribute('content'),
          previousMarker: existing.getAttribute(META_ATTR),
        });
        existing.setAttribute('content', content);
        existing.setAttribute(META_ATTR, '1');
        return;
      }

      const tag = document.createElement('meta');
      tag.setAttribute(attribute, key);
      tag.setAttribute('content', content);
      tag.setAttribute(META_ATTR, '1');
      document.head.appendChild(tag);
      snapshots.set(selector, {
        tag,
        created: true,
        previousContent: null,
        previousMarker: null,
      });
    };

    const description = typeof settings.description === 'string' ? settings.description.trim() : '';
    if (description) setMeta('name', 'description', description);

    const keywords = typeof settings.keywords === 'string' ? settings.keywords.trim() : '';
    if (keywords) setMeta('name', 'keywords', keywords);

    const ogTitle = typeof settings.ogTitle === 'string' && settings.ogTitle.trim()
      ? settings.ogTitle.trim()
      : typeof settings.title === 'string' && settings.title.trim()
        ? settings.title.trim()
        : title;
    if (ogTitle) setMeta('property', 'og:title', ogTitle);

    const ogDescription = typeof settings.ogDescription === 'string' && settings.ogDescription.trim()
      ? settings.ogDescription.trim()
      : description;
    if (ogDescription) setMeta('property', 'og:description', ogDescription);

    if (typeof settings.ogImage === 'string' && settings.ogImage.trim()) {
      setMeta('property', 'og:image', settings.ogImage.trim());
    }

    const robots = [
      noIndex || settings.noIndex ? 'noindex' : '',
      noIndex || settings.noFollow ? 'nofollow' : '',
    ].filter(Boolean).join(', ');
    if (robots) setMeta('name', 'robots', robots);

    let canonical: HTMLLinkElement | null = null;
    let canonicalCreated = false;
    let previousCanonicalHref: string | null = null;
    let previousCanonicalMarker: string | null = null;
    const canonicalUrl = typeof settings.canonicalUrl === 'string' ? settings.canonicalUrl.trim() : '';
    if (canonicalUrl) {
      const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (existing) {
        canonical = existing;
        previousCanonicalHref = existing.getAttribute('href');
        previousCanonicalMarker = existing.getAttribute(META_ATTR);
        existing.setAttribute('href', canonicalUrl);
        existing.setAttribute(META_ATTR, '1');
      } else {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        canonical.setAttribute('href', canonicalUrl);
        canonical.setAttribute(META_ATTR, '1');
        document.head.appendChild(canonical);
        canonicalCreated = true;
      }
    }

    return () => {
      document.title = previousTitle;
      for (const snapshot of snapshots.values()) {
        if (snapshot.created) {
          snapshot.tag.remove();
          continue;
        }
        if (snapshot.previousContent === null) snapshot.tag.removeAttribute('content');
        else snapshot.tag.setAttribute('content', snapshot.previousContent);
        if (snapshot.previousMarker === null) snapshot.tag.removeAttribute(META_ATTR);
        else snapshot.tag.setAttribute(META_ATTR, snapshot.previousMarker);
      }
      if (!canonical) return;
      if (canonicalCreated) {
        canonical.remove();
        return;
      }
      if (previousCanonicalHref === null) canonical.removeAttribute('href');
      else canonical.setAttribute('href', previousCanonicalHref);
      if (previousCanonicalMarker === null) canonical.removeAttribute(META_ATTR);
      else canonical.setAttribute(META_ATTR, previousCanonicalMarker);
    };
  }, [page, siteName, noIndex]);
}

/**
 * Neutral placeholder shown while the published page is being fetched. It is
 * deliberately plain: showing the built-in home and then swapping it out is
 * what caused the visible "old template" flash.
 */
export function PublishedHomeSkeleton() {
  return (
    <div className="flex w-full flex-col" aria-busy="true" aria-label="Loading the page">
      <div className="w-full animate-pulse bg-white">
        <div className="mx-auto w-full max-w-6xl px-6 py-16 sm:py-24">
          <div className="h-3 w-28 rounded bg-slate-100" />
          <div className="mt-6 h-10 w-3/4 rounded bg-slate-100 sm:h-12" />
          <div className="mt-4 h-4 w-2/3 rounded bg-slate-100" />
          <div className="mt-4 h-4 w-1/2 rounded bg-slate-100" />
          <div className="mt-8 h-11 w-40 rounded-xl bg-slate-100" />
        </div>
        <div className="h-px w-full bg-slate-100" />
        <div className="mx-auto grid w-full max-w-6xl gap-6 px-6 py-16 sm:grid-cols-3">
          {[0, 1, 2].map((key) => (
            <div key={key} className="h-32 rounded-2xl bg-slate-100" />
          ))}
        </div>
      </div>
    </div>
  );
}

export function PublishedHome({
  sections,
  settings,
  designTokens,
  fill = false,
}: {
  sections: PageSection[];
  settings?: PageSettings;
  designTokens?: PageDesignTokens;
  fill?: boolean;
}) {
  const tokens = designTokens ?? {};
  const style = {
    ...pageDesignCssVars(tokens),
    ...pageBackgroundStyle(settings ?? {}),
  } as React.CSSProperties;
  const fontCss = pageFontFaceCss(tokens);
  const customCss = typeof tokens.customCss === 'string' ? tokens.customCss.trim() : '';
  const responsiveCss = sections.map((section) => sectionResponsiveCss(section.id, section.style)).join('');

  return (
    <div className={`flex w-full flex-col ${fill ? 'min-h-full' : ''}`} style={style} data-storefront-page>
      {fontCss ? <style data-builder-fonts>{fontCss}</style> : null}
      {customCss ? <style data-builder-css>{customCss}</style> : null}
      {responsiveCss ? <style data-builder-responsive>{responsiveCss}</style> : null}
      {sections.map((section) => {
        const Component = getSectionComponent(section.category, section.variationId);
        if (!Component) return null;
        return (
          <div key={section.id} data-section-id={section.id}>
            <Component content={section.content} style={section.style} />
          </div>
        );
      })}
    </div>
  );
}
